/**
 * What an answer was built from, and the odds and ends that sit beside it.
 *
 * These came out of the old ChatPanel when it was deleted. They are not kit
 * components and should not become them: each one reaches into the chat store
 * and `AuditDialog` fetches its own report, where the kit's contract is data
 * in through props and no fetching. They belong beside the panel that uses
 * them.
 *
 * `AuditDialog` still styles itself with raw `alpha()` colours rather than
 * tokens — carried over as-is, so the move stays a move. Worth a pass of its
 * own, on a component people rely on to check a number before they repeat it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { format as formatSQL } from 'sql-formatter';
import { AppDialog } from '../../components/dialog';
import FileCard from '../../components/attachments/FileCard';
import ConnectorTypeIcon from '../../pages/copilot/components/ConnectorTypeIcon';
import type { CalculationAuditReport } from '../../pages/copilot/stores/useCopilotStore';
import { hydrateAuditById } from '../../pages/copilot/hooks/useArtifactHydration';
import { useChatStore, useChatStoreApi } from '../core/ChatStoreContext';

function formatSqlForDisplay(sql: string): string {
  if (!sql) return '';
  try {
    return formatSQL(sql, { language: 'sql' });
  } catch {
    return sql;
  }
}

function formatRequestForDisplay(query: string, requestType?: string): string {
  if (!query) return '';
  if ((requestType || '').toLowerCase() === 'api') {
    try {
      const parsed = JSON.parse(query);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return query;
    }
  }
  return formatSqlForDisplay(query);
}

/** Chart intent x/y may be a single string or an array of strings. */
function normalizeChartAxisFields(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).filter((s) => s.length > 0);
  }
  const s = String(value).trim();
  return s ? [s] : [];
}

export function HistoricalAttachmentChips({ attachmentIds }: { attachmentIds: string[] }) {
  const cache = useChatStore((s) => s.attachmentCache);
  return (
    <>
      {attachmentIds.map((id) => {
        const server = cache?.[id];
        if (server) {
          return <FileCard key={id} server={server} variant="compact" />;
        }
        return (
          <Chip
            key={id}
            size="small"
            variant="outlined"
            label={`File ${id.slice(0, 8)}…`}
            sx={{ height: 28, fontSize: '0.7rem' }}
          />
        );
      })}
    </>
  );
}

export function AuditDialog({ open, auditId, onClose }: { open: boolean; auditId: string | null; onClose: () => void }) {
  const theme = useTheme();
  const chatStore = useChatStoreApi();
  const report = useChatStore((s) => (auditId ? s.auditReports[auditId] : null)) as CalculationAuditReport | null;
  const threadTitle = useChatStore((s) => s.threadTitle);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoadError, setAuditLoadError] = useState<string | null>(null);
  const [copiedQueryKey, setCopiedQueryKey] = useState<string>('');

  useEffect(() => {
    if (!open || !auditId || report) return;
    setAuditLoading(true);
    setAuditLoadError(null);
    void hydrateAuditById(auditId, chatStore.getState())
      .catch((err) => {
        setAuditLoadError(err instanceof Error ? err.message : 'Failed to load audit');
      })
      .finally(() => setAuditLoading(false));
  }, [open, auditId, report, chatStore]);

  useEffect(() => {
    setCopiedQueryKey('');
  }, [auditId, open]);

  const handleCopyQuery = useCallback((sql: string, key: string) => {
    if (!sql) return;
    navigator.clipboard.writeText(sql);
    setCopiedQueryKey(key);
    setTimeout(() => setCopiedQueryKey(''), 1800);
  }, []);

  const auditPanelBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : alpha(theme.palette.common.black, 0.03);
  const sourceCardBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.background.paper;
  const sourceCardOutline =
    theme.palette.mode === 'dark'
      ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
      : `1px solid ${alpha(theme.palette.common.black, 0.06)}`;

  const stepHeader = (n: number, label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
      <Box
        sx={(t) => ({
          minWidth: 22,
          height: 22,
          borderRadius: `${t.ds.radius.sm}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          bgcolor: t.tokens.brand.primarySoft,
          color: t.tokens.brand.primary,
        })}
      >
        {n}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
        {label}
      </Typography>
    </Box>
  );

  const queryAccordionBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.07)
      : theme.palette.grey[100];
  const queryAccordionHoverBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.1)
      : theme.palette.grey[200];
  const hasForecastDetails = !!report?.story?.outputs?.forecastDetails?.length;
  const resultsStepNumber = hasForecastDetails ? 5 : 4;
  const auditAccordionSx = {
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.8 : 1)}`,
    borderRadius: 1,
    overflow: 'hidden',
    boxShadow: 'none',
    bgcolor: queryAccordionBg,
    '&:before': { display: 'none' },
    '& .MuiAccordionSummary-root': {
      bgcolor: queryAccordionBg,
      color: 'text.primary',
      '&:hover:not(.Mui-disabled)': { bgcolor: queryAccordionHoverBg },
    },
  } as const;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      icon={<TimelineOutlined sx={{ color: 'primary.main', opacity: 0.92 }} />}
      title="Sources and calculation trail"
      dividers
    >
        {!report ? (
          auditLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading calculation trail…
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color={auditLoadError ? 'error.main' : 'text.secondary'}>
              {auditLoadError || 'No audit details available for this response.'}
            </Typography>
          )
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.75 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.75, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
              >
                Thread
              </Typography>
              <Box
                sx={{
                  color: 'text.primary',
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: 1.45,
                  '& p': { m: 0 },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {(threadTitle && threadTitle.trim()) || report.question || ''}
                </ReactMarkdown>
              </Box>
            </Box>

            {report.story ? (
              <>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
                  >
                    Summary
                  </Typography>
                  {report.story.headline ? (
                    <Box
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        lineHeight: 1.65,
                        fontWeight: 400,
                        '& p': { m: 0 },
                        '& strong': { color: 'text.primary', fontWeight: 600 },
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.headline}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No summary for this run.
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.25,
                    bgcolor: auditPanelBg,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                  }}
                >
                  <Box sx={{ pb: 2 }}>
                    {stepHeader(1, 'Data sources')}
                    {report.story.dataSources?.summary
                      && !report.story.dataSources?.details?.length && (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 }, mb: 1 }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.dataSources.summary}</ReactMarkdown>
                      </Box>
                    )}
                    {!!report.story.dataSources?.details?.length && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {report.story.dataSources.details.map((d, idx) => (
                          <Box
                            key={`${d.name}-${idx}`}
                            sx={{
                              p: 1.5,
                              borderRadius: 1.5,
                              bgcolor: sourceCardBg,
                              boxShadow: 'none',
                              border: sourceCardOutline,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                              <ConnectorTypeIcon connectorType={d.connectorType} size={22} />
                              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                                {d.name || 'Source'}
                                {d.connectorType ? (
                                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                                    {` · ${d.connectorType}`}
                                  </Typography>
                                ) : null}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={(d.requestType || 'sql').toUpperCase()}
                                sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.04em' }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                              {(d.records || 0).toLocaleString()} rows · {(d.columns || 0).toLocaleString()} columns
                            </Typography>
                            {!!d.query && (
                              <Accordion disableGutters sx={auditAccordionSx}>
                                <AccordionSummary expandIcon={<ExpandMoreRounded fontSize="small" />} sx={{ minHeight: 40, px: 1.25 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                      {(d.requestType || 'sql').toLowerCase() === 'api' ? 'API request' : 'SQL query'}
                                    </Typography>
                                    <Tooltip title={copiedQueryKey === `src-${idx}` ? 'Copied!' : 'Copy'}>
                                      <IconButton
                                        size="small"
                                        sx={{ ml: 'auto', mr: 0.5 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyQuery(
                                            formatRequestForDisplay(d.query || '', d.requestType || 'sql'),
                                            `src-${idx}`,
                                          );
                                        }}
                                      >
                                        {copiedQueryKey === `src-${idx}` ? (
                                          <CheckCircleRounded fontSize="small" color="success" />
                                        ) : (
                                          <ContentCopyRounded fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails
                                  sx={{
                                    p: 0,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[50],
                                  }}
                                >
                                  <SyntaxHighlighter
                                    language={((d.requestType || 'sql').toLowerCase() === 'api') ? 'json' : 'sql'}
                                    style={theme.palette.mode === 'dark' ? vscDarkPlus : vs}
                                    customStyle={{
                                      margin: 0,
                                      padding: '10px 12px',
                                      fontSize: '0.75rem',
                                      borderRadius: 0,
                                      ...(theme.palette.mode === 'light'
                                        ? { background: `${theme.palette.grey[50]} !important` as const }
                                        : {}),
                                    }}
                                  >
                                    {formatRequestForDisplay(d.query || '', d.requestType || 'sql')}
                                  </SyntaxHighlighter>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />

                  <Box sx={{ py: 2 }}>
                    {stepHeader(2, 'Fetched and prepared in workspace')}
                    {report.story.staging?.summary && (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 } }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.staging.summary}</ReactMarkdown>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />

                  <Box sx={{ py: 2 }}>
                    {stepHeader(3, 'Calculations')}
                    {report.story.calculation?.summary && (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 }, mb: 1 }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.calculation.summary}</ReactMarkdown>
                      </Box>
                    )}
                    {!!report.story.calculation?.steps?.length && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {report.story.calculation.steps.map((step, idx) => {
                          const qKey = `calc-${idx}`;
                          return (
                            <Accordion key={`${idx}-${step.tool}`} disableGutters sx={auditAccordionSx}>
                              <AccordionSummary expandIcon={<ExpandMoreRounded fontSize="small" />} sx={{ minHeight: 40, px: 1.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Workspace query {idx + 1}
                                  </Typography>
                                  {!!step.sql && (
                                    <Tooltip title={copiedQueryKey === qKey ? 'Copied!' : 'Copy query'}>
                                      <IconButton
                                        size="small"
                                        sx={{ ml: 'auto', mr: 0.5 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyQuery(step.sql || '', qKey);
                                        }}
                                      >
                                        {copiedQueryKey === qKey ? (
                                          <CheckCircleRounded fontSize="small" color="success" />
                                        ) : (
                                          <ContentCopyRounded fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails
                                sx={{
                                  p: 0,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                  bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[50],
                                }}
                              >
                                <SyntaxHighlighter
                                  language="sql"
                                  style={theme.palette.mode === 'dark' ? vscDarkPlus : vs}
                                  customStyle={{
                                    margin: 0,
                                    padding: '10px 12px',
                                    fontSize: '0.75rem',
                                    borderRadius: 0,
                                    ...(theme.palette.mode === 'light'
                                      ? { background: `${theme.palette.grey[50]} !important` as const }
                                      : {}),
                                  }}
                                >
                                  {formatSqlForDisplay(step.sql || '')}
                                </SyntaxHighlighter>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Box>
                    )}
                  </Box>

                  {hasForecastDetails && (
                    <>
                      <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />
                      <Box sx={{ py: 2 }}>
                        {stepHeader(4, 'Forecast model report')}
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6, mb: 1 }}>
                          We backtested multiple models on recent history and selected the best one using a weighted score (RMSE + MAE + WAPE + tail error).
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {report.story.outputs?.forecastDetails?.map((fc, fidx) => {
                            const fp = fc.firstPeriod;
                            const conf =
                              fc.confidenceLevelPct != null
                                ? `${fc.confidenceLevelPct}%`
                                : fc.confidenceLevel != null
                                  ? `${Math.round(fc.confidenceLevel * 100)}%`
                                  : null;
                            const candidates = (fc.candidateModels || []).filter((m) => !!m.model);
                            return (
                              <Box
                                key={`${fc.title}-${fidx}`}
                                sx={{
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  bgcolor: sourceCardBg,
                                  border: sourceCardOutline,
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {fc.title || `Forecast ${fidx + 1}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.55 }}>
                                  {fc.selectedModel ? `Selected model: ${fc.selectedModel}` : 'Forecast model'}
                                  {fc.horizon != null && fc.horizon > 0 && fc.timeUnit
                                    ? ` · ${fc.horizon} ${fc.timeUnit}${fc.horizon === 1 ? '' : 's'} ahead`
                                    : ''}
                                  {conf ? ` · ${conf} interval` : ''}
                                </Typography>
                                {!!fc.reliability && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block">
                                    Reliability: {fc.reliability}
                                    {fc.historyPoints ? ` · based on ${fc.historyPoints.toLocaleString()} history points` : ''}
                                  </Typography>
                                )}
                                {fc.selectedModelMetrics && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.3 }}>
                                    Selected metrics:
                                    {fc.selectedModelMetrics.selectionScore != null ? ` score ${Number(fc.selectedModelMetrics.selectionScore).toFixed(3)}` : ''}
                                    {fc.selectedModelMetrics.rmse != null ? ` · RMSE ${Number(fc.selectedModelMetrics.rmse).toFixed(3)}` : ''}
                                    {fc.selectedModelMetrics.mae != null ? ` · MAE ${Number(fc.selectedModelMetrics.mae).toFixed(3)}` : ''}
                                    {fc.selectedModelMetrics.wape != null ? ` · WAPE ${(Number(fc.selectedModelMetrics.wape) * 100).toFixed(1)}%` : ''}
                                  </Typography>
                                )}
                                {!!candidates.length && (
                                  <Box sx={{ mt: 0.8, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      Backtests
                                    </Typography>
                                    {candidates.map((m, midx) => {
                                      const isWinner = !!fc.selectedModel && m.model === fc.selectedModel;
                                      return (
                                        <Box
                                          key={`${m.model}-${midx}`}
                                          sx={{
                                            px: 0.8,
                                            py: 0.55,
                                            borderRadius: 1,
                                            border: `1px solid ${isWinner ? alpha(theme.palette.primary.main, 0.45) : alpha(theme.palette.divider, 0.75)}`,
                                            bgcolor: isWinner ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08) : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.9,
                                            flexWrap: 'wrap',
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: isWinner ? 700 : 600, color: isWinner ? 'primary.main' : 'text.primary' }}>
                                            {m.model}
                                          </Typography>
                                          {m.selectionScore != null && (
                                            <Typography variant="caption" color="text.secondary">score {Number(m.selectionScore).toFixed(3)}</Typography>
                                          )}
                                          {m.rmse != null && (
                                            <Typography variant="caption" color="text.secondary">RMSE {Number(m.rmse).toFixed(3)}</Typography>
                                          )}
                                          {m.mae != null && (
                                            <Typography variant="caption" color="text.secondary">MAE {Number(m.mae).toFixed(3)}</Typography>
                                          )}
                                          {m.wape != null && (
                                            <Typography variant="caption" color="text.secondary">WAPE {(Number(m.wape) * 100).toFixed(1)}%</Typography>
                                          )}
                                          {m.p95AbsError != null && (
                                            <Typography variant="caption" color="text.secondary">P95 {Number(m.p95AbsError).toFixed(3)}</Typography>
                                          )}
                                          {isWinner && (
                                            <Chip size="small" label="Selected" color="primary" sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.02em' }} />
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                                {(fc.timeColumn || fc.targetColumn || fc.dataWindowStart || fc.dataWindowEnd) && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.45 }}>
                                    {fc.timeColumn ? `Time: ${fc.timeColumn}` : ''}
                                    {fc.timeColumn && fc.targetColumn ? ' · ' : ''}
                                    {fc.targetColumn ? `Target: ${fc.targetColumn}` : ''}
                                    {(fc.timeColumn || fc.targetColumn) && (fc.dataWindowStart || fc.dataWindowEnd) ? ' · ' : ''}
                                    {(fc.dataWindowStart || fc.dataWindowEnd)
                                      ? `Data window: ${fc.dataWindowStart || '?'} to ${fc.dataWindowEnd || '?'}`
                                      : ''}
                                  </Typography>
                                )}
                                {(fc.droppedPoints || fc.rawRows || fc.preparedPoints || fc.seasonLength) ? (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.3 }}>
                                    Assumptions:
                                    {fc.rawRows ? ` raw rows ${fc.rawRows.toLocaleString()}` : ''}
                                    {fc.preparedPoints ? `, prepared points ${fc.preparedPoints.toLocaleString()}` : ''}
                                    {fc.droppedPoints ? `, dropped ${fc.droppedPoints.toLocaleString()}` : ''}
                                    {fc.seasonLength ? `, season length ${fc.seasonLength}` : ''}
                                  </Typography>
                                ) : null}
                                {!!fc.caveats?.length && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.3 }}>
                                    Caveats: {fc.caveats.join(' ')}
                                  </Typography>
                                )}
                                {fp && (fp.prediction != null || fp.period != null) && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.35 }}>
                                    Next step
                                    {fp.period != null && fp.period !== '' ? ` (${fp.period})` : ''}
                                    {fp.prediction != null
                                      ? `: ${fp.prediction}${fp.lower != null && fp.upper != null ? ` (range ${fp.lower}–${fp.upper})` : ''}`
                                      : ''}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </>
                  )}

                  <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />

                  <Box sx={{ pt: 2 }}>
                    {stepHeader(resultsStepNumber, 'Results')}
                    {hasForecastDetails ? (
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6, mb: 1 }}>
                        Final visual/table outputs from the selected forecast model are shown here.
                      </Typography>
                    ) : (
                      report.story.outputs?.summary && (
                        <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 }, mb: 1 }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.outputs.summary}</ReactMarkdown>
                        </Box>
                      )
                    )}
                    {!!report.story.outputs?.chartDetails?.length && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {report.story.outputs.chartDetails.map((ch, idx) => {
                          const xFields = normalizeChartAxisFields(ch.xFields);
                          const yFields = normalizeChartAxisFields(ch.yFields);
                          return (
                            <Box
                              key={`${ch.title}-${idx}`}
                              sx={{
                                p: 1.25,
                                borderRadius: 1.5,
                                bgcolor: sourceCardBg,
                                border: sourceCardOutline,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {ch.title || `Chart ${idx + 1}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
                                {ch.type || 'chart'} · {(ch.records || 0).toLocaleString()} rows
                                {ch.timeUnit ? ` · ${ch.timeUnit}` : ''}
                              </Typography>
                              {xFields.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div" display="block">
                                  X: {xFields.join(', ')}
                                </Typography>
                              )}
                              {yFields.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div" display="block">
                                  Y: {yFields.join(', ')}
                                </Typography>
                              )}
                              {!!ch.columns?.length && (
                                <Typography variant="caption" color="text.secondary" component="div" display="block">
                                  Fields: {ch.columns.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {hasForecastDetails && !report.story.outputs?.chartDetails?.length && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        No chart artifact was generated for this run. Output type: {(report.story.outputs?.resultTypes || ['table']).join(', ')}.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No summarized story available yet for this run.
              </Typography>
            )}
          </Box>
        )}
    </AppDialog>
  );
}

export function ContextUsageBar() {
  const contextUsage = useChatStore((s) => s.contextUsage);

  if (!contextUsage?.trimmed) return null;

  return (
    <Typography
      variant="caption"
      sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: 0.5, textAlign: 'center', fontStyle: 'italic' }}
    >
      Earlier messages have been summarized to keep the conversation flowing
    </Typography>
  );
}
